import React, { FC } from 'react';

import SemMonthFilterProvider, {
  useSemMonthFilter,
} from '../../../../components/SEM/Filters/SemMonthFilter/MonthFilterProvider';
import PriceFilterProvider, {
  usePriceFilter,
  useAppliedPriceFilter,
} from '../../../../components/Filters/Components/PriceFilter/Provider';
import StopDurationFilterProvider, {
  useStopDurationFilter,
} from '../../../../components/Filters/Components/StopDurationFilter/Provider';
import ConnectionFilterProvider, {
  useConnectionFilter,
} from '../../../../components/Filters/Components/StopAirportFilter/Provider';
import {
  AirportFilterProvider,
  CarrierFilterProvider,
  MaxStopsFilterProvider,
  TimeFilterProvider,
  useTimeFilter,
} from '../../../../components/Filters';
import SemWeekDayFilterProvider, {
  useSemWeekDayFilter,
} from '../../../../components/SEM/Filters/SemWeekDayFilter/Provider';
import TransportTypeFilterProvider, {
  useAppliedTransportTypeFilter,
  useTransportTypeFilter,
} from '../../../../components/Filters/Components/TransportTypeFilter/Provider';
import {
  useAirportFilterPredicate,
  useAppliedAirportFilter,
} from '../../../../components/Filters/Components/AirportFilter/Provider';
import {
  useCarrierFilterPredicate,
  useAppliedCarrierFilter,
} from '../../../../components/Filters/Components/CarrierFilter/Provider';
import {
  useConnectionThresholdFilterPredicate,
  useAppliedConnectionThresholdFilter,
} from '../../../../components/Filters/Components/MaxStopsFilter/Provider';
import SemDurationFilterProvider, {
  useSemDurationFilter,
} from '../../../../components/Filters/Components/DurationFilter/Provider';
import { useAppliedTimeFilter } from '../../../../components/Filters/Components/TimeFilter/Provider';
import SemTripTypeFilterProvider from '../../../../components/SEM/Filters/SemTripTypeFilter/TripTypeFilterProvider';

type FilterRequestType = {
  transportTypes;
  timeRanges;
  maxDurations;
  connectionThresholdPredicate;
  connectionsPredicate;
  airportsPredicate;
  carriersPredicate;
  priceRange;
  monthsSelected;
  weekDaysSelected;
  durationRangeSelected;
};

export const useSemFilterRequest = (): FilterRequestType => {
  const { transportTypes } = useTransportTypeFilter();
  const [timeRanges] = useTimeFilter();
  const [maxDurations] = useStopDurationFilter();
  const { predicate: connectionsPredicate } = useConnectionFilter();
  const airportsPredicate = useAirportFilterPredicate();
  const carriersPredicate = useCarrierFilterPredicate();
  const connectionThresholdPredicate = useConnectionThresholdFilterPredicate();
  const [priceRange] = usePriceFilter();
  const { monthsSelected } = useSemMonthFilter();
  const { weekDaysSelected } = useSemWeekDayFilter();
  const { durationRangeSelected } = useSemDurationFilter();

  return {
    transportTypes,
    timeRanges,
    maxDurations,
    connectionThresholdPredicate,
    connectionsPredicate,
    airportsPredicate,
    carriersPredicate,
    priceRange,
    monthsSelected,
    weekDaysSelected,
    durationRangeSelected,
  };
};

export const useAreFiltersApplied = (): boolean => {
  const hasTransporTypeFilterApplied = useAppliedTransportTypeFilter();
  const hasPriceFilterApplied = useAppliedPriceFilter();
  const hasTimeFilterApplied = useAppliedTimeFilter();
  const hasMaxStopsApplied = useAppliedConnectionThresholdFilter();
  const hasAirportFilterApplied = useAppliedAirportFilter();
  const hasCarrierFilterApplied = useAppliedCarrierFilter();
  const { isSemMonThFilterApplied } = useSemMonthFilter();
  const { isSemWeekDaysFilterApplied } = useSemWeekDayFilter();
  const { isSemDurationFilterApplied } = useSemDurationFilter();
  return (
    hasTransporTypeFilterApplied ||
    hasPriceFilterApplied ||
    hasTimeFilterApplied ||
    hasMaxStopsApplied ||
    hasAirportFilterApplied ||
    hasCarrierFilterApplied ||
    isSemMonThFilterApplied ||
    isSemWeekDaysFilterApplied ||
    isSemDurationFilterApplied
  );
};

const SemFiltersProvider: FC = ({ children }) => {
  return (
    <TransportTypeFilterProvider>
      <PriceFilterProvider>
        <TimeFilterProvider>
          <MaxStopsFilterProvider>
            <StopDurationFilterProvider>
              <ConnectionFilterProvider>
                <AirportFilterProvider>
                  <CarrierFilterProvider>
                    <SemMonthFilterProvider>
                      <SemWeekDayFilterProvider>
                        <SemDurationFilterProvider>
                          <SemTripTypeFilterProvider>
                            {children}
                          </SemTripTypeFilterProvider>
                        </SemDurationFilterProvider>
                      </SemWeekDayFilterProvider>
                    </SemMonthFilterProvider>
                  </CarrierFilterProvider>
                </AirportFilterProvider>
              </ConnectionFilterProvider>
            </StopDurationFilterProvider>
          </MaxStopsFilterProvider>
        </TimeFilterProvider>
      </PriceFilterProvider>
    </TransportTypeFilterProvider>
  );
};

export default SemFiltersProvider;
